// Ein/Aus-Schalter der Portale (Vorgabe Patrick: jede Funktion erklärt und abschaltbar).
// app_settings 'portale_funktionen' = { mieterportal: bool, eigentuemerportal: bool, testkonten: [id] };
// fehlt ein Schlüssel, ist das Portal AUS. Ändern dürfen nur Admins (RLS, Präfix 'portale_').
// Vorgabe Patrick 23.10.2026: nie standardmäßig an; zum Testen nur Profile in `testkonten`.
import { supabase } from '../supabase/client';

export const PORTALE_KEY = 'portale_funktionen';
export type PortalArt = 'mieterportal' | 'eigentuemerportal';
export type PortalSchalter = Record<PortalArt, boolean> & { testkonten: number };

export const PORTAL_ERKLAERUNG: Record<PortalArt, { titel: string; aus: string; was: string; nicht: string }> = {
  mieterportal: {
    titel: 'Mieterportal',
    aus: 'Mieter mit Portal-Zugang sehen nach dem Login nur „Ihr Portal ist bald verfügbar“.',
    was: 'Mieter mit Portal-Zugang sehen nach dem Login ihr eigenes Portal: Wohnung, eigene Meldungen, Termine, freigegebene Dokumente und die Miete. Über „Schaden melden“ entsteht ein Ticket für die Verwaltung.',
    nicht: 'Mieter sehen keine anderen Mieter, keine internen Notizen, keine Bankdaten des Eigentümers und keine Dokumente, die nicht für sie freigegeben sind. Den Verwaltungsbereich erreichen sie nie.',
  },
  eigentuemerportal: {
    titel: 'Eigentümerportal',
    aus: 'Eigentümer mit Portal-Zugang sehen nach dem Login nur „Ihr Portal ist bald verfügbar“.',
    was: 'Eigentümer mit Portal-Zugang sehen ihre Objekte und Einheiten, vorhandene Finanzzahlen und freigegebene Dokumente. Rechnungen, die ihre Freigabe brauchen, können sie freigeben oder mit Begründung ablehnen.',
    nicht: 'Eigentümer können sonst nichts ändern, sehen keine anderen Eigentümer und keine internen Notizen. Den Verwaltungsbereich erreichen sie nie.',
  },
};

/** Gespeicherten Wert lesen: nur ausdrücklich true ist an. */
export function leseSchalter(wert: unknown): PortalSchalter {
  const o = wert && typeof wert === 'object' && !Array.isArray(wert) ? wert as Record<string, unknown> : {};
  return {
    mieterportal: o.mieterportal === true,
    eigentuemerportal: o.eigentuemerportal === true,
    testkonten: Array.isArray(o.testkonten) ? o.testkonten.length : 0,
  };
}

/** null = Eintrag fehlt (Datenbank-Freigabe noch nicht eingespielt). */
export async function ladePortalSchalter(): Promise<PortalSchalter | null> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', PORTALE_KEY).maybeSingle();
  if (error || !data) return null;
  return leseSchalter((data as { value: unknown }).value);
}

/** Ändert nur den einen Schalter; alle anderen Schlüssel (auch die Testkonten) bleiben unverändert. */
export async function speicherePortalSchalter(art: PortalArt, an: boolean): Promise<string | null> {
  const { data, error: lesefehler } = await supabase.from('app_settings').select('value').eq('key', PORTALE_KEY).maybeSingle();
  if (lesefehler || !data) return 'Der Schalter ist noch nicht eingerichtet.';
  const roh = (data as { value: unknown }).value;
  const alt = roh && typeof roh === 'object' && !Array.isArray(roh) ? roh as Record<string, unknown> : {};
  const { error } = await supabase.from('app_settings').update({ value: { ...alt, [art]: an } }).eq('key', PORTALE_KEY);
  return error ? error.message : null;
}

/** #606: Liste der einzeln freigeschalteten Profile (Testphase) – unbekannte Werte werden ignoriert. */
export function testkontenListe(wert: unknown): string[] {
  const o = wert && typeof wert === 'object' && !Array.isArray(wert) ? wert as Record<string, unknown> : {};
  return Array.isArray(o.testkonten) ? o.testkonten.filter((x): x is string => typeof x === 'string') : [];
}

/** Neue Liste nach dem Umschalten einer Person (ohne Doppelte, Reihenfolge bleibt). */
export function mitTestkonto(liste: readonly string[], profilId: string, an: boolean): string[] {
  const ohne = liste.filter(id => id !== profilId);
  return an ? [...ohne, profilId] : ohne;
}

export interface TestphaseStand { portalAn: boolean; frei: boolean }

/** #606: Ist das Portal für dieses Profil einzeln freigeschaltet (bzw. für alle an)? null = nicht lesbar. */
export async function ladeTestphase(art: PortalArt, profilId: string): Promise<TestphaseStand | null> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', PORTALE_KEY).maybeSingle();
  if (error || !data) return null;
  const wert = (data as { value: unknown }).value;
  return { portalAn: leseSchalter(wert)[art], frei: testkontenListe(wert).includes(profilId) };
}

/**
 * #606: Portal nur für diese eine Person freischalten bzw. wieder wegnehmen (Testphase).
 * Ändert nur die Liste `testkonten`; die Portal-Schalter für alle bleiben, wie sie sind.
 * Die Datenbank lässt nur Admins schreiben – ohne Treffer gibt es eine Meldung statt stillem Nichts.
 */
export async function setzeTestphase(profilId: string, an: boolean): Promise<string | null> {
  const { data, error: lesefehler } = await supabase.from('app_settings').select('value').eq('key', PORTALE_KEY).maybeSingle();
  if (lesefehler || !data) return 'Der Portal-Schalter ist noch nicht eingerichtet.';
  const roh = (data as { value: unknown }).value;
  const alt = roh && typeof roh === 'object' && !Array.isArray(roh) ? roh as Record<string, unknown> : {};
  const { data: neu, error } = await supabase.from('app_settings')
    .update({ value: { ...alt, testkonten: mitTestkonto(testkontenListe(roh), profilId, an) } })
    .eq('key', PORTALE_KEY).select('key');
  if (error) return error.message;
  return neu && neu.length ? null : 'Nur Admins können das Portal freischalten.';
}

/**
 * #606: Nach dem Anlegen eines Zugangs das Portal gleich für diese Person freischalten (Testphase),
 * solange das Portal für alle aus ist. Gibt eine Fehlermeldung zurück oder null (auch wenn nichts nötig war).
 */
export async function testphaseFuerDatensatz(art: PortalArt, tabelle: 'mieter' | 'eigentuemer', datensatzId: string): Promise<string | null> {
  const { data } = await supabase.from(tabelle).select('profile_id').eq('id', datensatzId).maybeSingle();
  const pid = (data as { profile_id?: string | null } | null)?.profile_id ?? null;
  if (!pid) return 'Zugang ohne Verknüpfung – bitte Seite neu laden';
  const stand = await ladeTestphase(art, pid);
  if (!stand) return 'Portal-Schalter nicht lesbar';
  if (stand.portalAn || stand.frei) return null;
  return setzeTestphase(pid, true);
}
